import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import Quill from 'quill/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './AiCopilotChatPanel.module.scss';
import type { Source } from './useAiCopilot';
import useAiCopilot from './useAiCopilot';
import RightPanel from '../app/layout/RightPanel';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import OpacityMotionTransition from '../components/OpacityMotionTransition';
import SVGIcon from '../components/SVGIcon';
import { useMarkdownToHTML } from '../components/useParsedMarkdown';
import type { Binding } from '../editor/BaseKeyboard';
import QuillComponent from '../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import findClosestScrollingContainer from '../editor/utils/findClosestScrollingContainer';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import aiIcon from '../icons/non-prefilled/16x16/ai.svg';
import arrowDownIcon from '../icons/non-prefilled/16x16/arrowDown.svg';
import BetaBadge from '../organization/BetaBadge';
import PostPill from '../post/PostPill';
import isContentEmpty from '../post/utils/isContentEmpty';
import { deltaToText } from '../utils/text/deltaText';

const PADDING_BOTTOM = parseInt(styles.paddingBottom, 10);
const HALF_SOURCES_WIDTH = 139;

type SystemMessage = {
  type: 'system';
  text: string;
  id: string;
  responseId: string;
  sources?: Source[];
};

type UserMessage = {
  type: 'user';
  text: string;
  id: string;
};

type LoadingMessage = {
  type: 'loading';
  id: string;
  responseId?: string;
};

type Conversation = [UserMessage, SystemMessage | LoadingMessage];

const DATA_MESSAGE_ID_ATTRIBUTE = 'data-message-id';

const getMessageElementById = (id: string) => {
  return document.querySelector<HTMLElement>(
    `[${DATA_MESSAGE_ID_ATTRIBUTE}="${id}"]`,
  );
};

interface MessageProps {
  message: Conversation[number];
  markdownToHTML: (markdown: string) => string | null;
}

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const Message = ({ message, markdownToHTML }: MessageProps) => (
  <div
    className={classNames(styles.message, styles[message.type])}
    {...{ [DATA_MESSAGE_ID_ATTRIBUTE]: message.id }}
  >
    {message.type === 'loading' ? (
      <div>Searching&hellip;</div>
    ) : (
      <div
        dangerouslySetInnerHTML={{
          __html: markdownToHTML(message.text) ?? '',
        }}
      />
    )}
    {message.type === 'system' && message.sources && (
      <div className={styles.sources}>
        {message.sources.map((source) => (
          <PostPill
            size="small"
            key={source.id}
            post={source}
            newTab
            maxWidth={
              message.sources && message.sources.length > 2
                ? HALF_SOURCES_WIDTH
                : 2 * HALF_SOURCES_WIDTH
            }
          />
        ))}
      </div>
    )}
  </div>
);

interface MessageComposerProps {
  isGenerating: boolean;
  onSend: () => void;
}

const MessageComposer = ({ isGenerating, onSend }: MessageComposerProps) => {
  const { quill } = useQuill();
  const [hasInputContent, setHasInputContent] = useState(false);

  const onSendEvent = useEffectEvent(onSend);

  useEffect(() => {
    if (!quill) return undefined;

    const handleTextChange = () => {
      setHasInputContent(isContentEmpty(quill.getContents().ops));
    };

    quill.on(Quill.events.TEXT_CHANGE, handleTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) return undefined;

    const bindings: Binding[] = [
      {
        key: 'Enter',
        handler() {
          onSendEvent();
        },
      },
    ];

    bindings.forEach((binding) => {
      // @ts-expect-error
      quill.keyboard.prependBinding(binding);
    });

    return () => {
      bindings.forEach((binding) => {
        // @ts-expect-error
        quill.keyboard.removeBinding(binding);
      });
    };
  }, [quill]);

  return (
    <div className={styles.messageComposer}>
      <div className={styles.inputContainer}>
        <QuillComponent
          noTitleLine
          namespace="aiCopilotChat"
          contentPlaceholder="Ask anything"
          readOnly={false}
          autoFocus
        />
      </div>
      <div className={styles.actions}>
        <div className={styles.actionsLeft}>
          {/* TODO: Placeholder for future features */}
          <Button icon={addIcon} className={styles.iconButton} />
          <Button icon={aiIcon} className={styles.iconButton} />
        </div>
        <Button
          size="small"
          variant="primary"
          disabled={hasInputContent || isGenerating}
          onClick={onSend}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

interface ScrollToBottomButtonProps {
  isScrolledToBottom: boolean;
  onClick: () => void;
}

const ScrollToBottomButton = ({
  isScrolledToBottom,
  onClick,
}: ScrollToBottomButtonProps) => {
  return (
    <AnimatePresence>
      {!isScrolledToBottom && (
        <OpacityMotionTransition
          className={styles.scrollToBottomButtonContainer}
        >
          <Button
            aria-label="Scroll to bottom"
            onClick={onClick}
            className={styles.scrollToBottomButton}
          >
            <SVGIcon size={16}>{arrowDownIcon}</SVGIcon>
          </Button>
        </OpacityMotionTransition>
      )}
    </AnimatePresence>
  );
};

const useIsScrolledToBottom = (containerRef: React.RefObject<HTMLElement>) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  useEffect(() => {
    const messagesContainer = containerRef.current;
    if (!messagesContainer) return undefined;

    const scrollContainer = findClosestScrollingContainer(containerRef.current);
    if (!scrollContainer) return undefined;

    const handleScrollOrResize = () => {
      setIsScrolledToBottom(
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - PADDING_BOTTOM,
      );
    };

    const observer = new ResizeObserver(handleScrollOrResize);
    observer.observe(messagesContainer);

    scrollContainer.addEventListener('scroll', handleScrollOrResize);

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener('scroll', handleScrollOrResize);
    };
  }, [containerRef]);

  return isScrolledToBottom;
};

const scrollToMessage = (messageId: string, messagesContainer: HTMLElement) => {
  const messageElement = getMessageElementById(messageId);
  if (!messageElement) return;

  const scrollContainer = findClosestScrollingContainer(messageElement);
  if (!scrollContainer) return;

  const messageRect = messageElement.getBoundingClientRect();
  const messagesContainerRect = messagesContainer.getBoundingClientRect();
  const scrollTop = messageRect.top - messagesContainerRect.top - 16;

  scrollContainer.scrollTo({
    top: scrollTop,
    behavior: 'smooth',
  });
};

const AiCopilotChatPanel = () => {
  const markdownToHTML = useMarkdownToHTML();
  const { quill } = useQuill();

  const footerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);

  const { sendMessage, isGenerating } = useAiCopilot({
    onStart: (responseId) => {
      setConversations((prev) => {
        return prev.map((conversation) => {
          const [userMessage, answerMessage] = conversation;
          if (answerMessage.type === 'loading') {
            return [
              userMessage,
              {
                ...answerMessage,
                responseId,
              },
            ];
          }

          return conversation;
        });
      });
    },
    onAnswerText: (responseId, text) => {
      setConversations((prev) => {
        return prev.map((conversation) => {
          const [userMessage, answerMessage] = conversation;

          if (
            answerMessage.type === 'loading' &&
            answerMessage.responseId === responseId
          ) {
            return [
              userMessage,
              {
                type: 'system',
                text,
                id: answerMessage.id,
                responseId,
              },
            ];
          }

          if (
            answerMessage.type === 'system' &&
            answerMessage.responseId === responseId
          ) {
            return [
              userMessage,
              {
                ...answerMessage,
                text: answerMessage.text + text,
              },
            ];
          }

          return conversation;
        });
      });
    },
    onFinish: (responseId, sources, text) => {
      setConversations((prev) => {
        return prev.map((conversation) => {
          const [userMessage, answerMessage] = conversation;

          if (
            answerMessage.type === 'system' &&
            answerMessage.responseId === responseId
          ) {
            return [userMessage, { ...answerMessage, sources, text }];
          }

          return conversation;
        });
      });
    },
  });

  const handleSend = useCallback(() => {
    if (!quill) return;

    const text = deltaToText(quill.getContents().ops);
    if (!text) return;

    if (sendMessage(text)) {
      const userMessage = {
        type: 'user' as const,
        text,
        id: crypto.randomUUID(),
      };

      const loadingMessage = {
        type: 'loading' as const,
        id: crypto.randomUUID(),
      };

      setConversations((prev) => [...prev, [userMessage, loadingMessage]]);
      quill.setContents([]);

      setTimeout(() => {
        const messagesContainer = messagesContainerRef.current;
        if (!messagesContainer) return;
        scrollToMessage(userMessage.id, messagesContainer);
      });
    }
  }, [quill, sendMessage]);

  const handleScrollToBottom = useCallback(() => {
    const scrollContainer = findClosestScrollingContainer(
      messagesContainerRef.current,
    );
    if (!scrollContainer) return;
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  const isScrolledToBottom = useIsScrolledToBottom(messagesContainerRef);

  const [measurements, setMeasurements] = useState({
    headerHeight: 0,
    footerHeight: 0,
  });

  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const footer = footerRef.current;
    if (!footer) return;

    setMeasurements({
      headerHeight: messagesContainer.offsetTop,
      footerHeight: footer.clientHeight,
    });
  }, []);

  return (
    <RightPanel.Inner
      header={
        <RightPanel.NewHeader
          title={
            <>
              AI Copilot
              <span className={styles.badge}>
                <BetaBadge />
              </span>
            </>
          }
          iconButtons={[
            {
              icon: addIcon,
              onClick: () => {
                setConversations([]);
              },
              tooltip: 'Start a new chat',
            },
          ]}
        />
      }
      footer={
        <div ref={footerRef} className={styles.footer}>
          <ScrollToBottomButton
            isScrolledToBottom={isScrolledToBottom}
            onClick={handleScrollToBottom}
          />

          <MessageComposer isGenerating={isGenerating} onSend={handleSend} />
        </div>
      }
    >
      <div
        ref={messagesContainerRef}
        className={classNames(styles.messagesContainer, {
          [styles.hasMessages]: conversations.length > 0,
        })}
        style={
          {
            '--header-height': `${measurements.headerHeight}px`,
            '--footer-height': `${measurements.footerHeight}px`,
          } as React.CSSProperties
        }
      >
        {conversations.map((conversation) => (
          <div key={conversation[0].id} className={styles.conversation}>
            {conversation.map(
              (message) =>
                !!markdownToHTML && (
                  <Message
                    key={message.id}
                    message={message}
                    markdownToHTML={markdownToHTML}
                  />
                ),
            )}
          </div>
        ))}
        {conversations.length === 0 && (
          <EmptyState imageName="aiCopilot" noMargin />
        )}
      </div>
    </RightPanel.Inner>
  );
};

export default withQuillContextProvider(AiCopilotChatPanel);
